import Footer from "@/components/Footer";
import Navbar from "@/components/Navbar";
import SeoTags from "@/components/SeoTags";
import { Button } from "@/components/ui/button";
import {
  SEO_DEFAULT_ORIGIN,
  SEO_DEFAULT_SOCIAL_IMAGE,
  type SeoMetadata,
} from "@shared/seo";
import {
  ArrowRight,
  LifeBuoy,
  MessageSquareMore,
  ShieldCheck,
} from "lucide-react";
import { Link } from "wouter";

const SUPPORT_EMAIL = "support@techfare.com";

const SUPPORT_SUMMARY_CARDS = [
  {
    title: "Help that stays practical",
    description:
      "This page is organized around the issues people hit most often: understanding price movement, managing listings, and navigating marketplace activity.",
    icon: LifeBuoy,
  },
  {
    title: "Context speeds up support",
    description:
      "The fastest path to a clear answer is sharing the product model, listing URL, screenshots, and any offer or sale details alongside your question.",
    icon: MessageSquareMore,
  },
  {
    title: "Policies built for marketplace trust",
    description:
      "The full legal policies now live on dedicated pages so users can review privacy and marketplace terms in much more detail.",
    icon: ShieldCheck,
  },
] as const;

const HELP_CENTER_TOPICS = [
  {
    title: "How should I read market prices?",
    description:
      "Use current market price as the broad benchmark, then compare it with the lowest ask, highest bid, and recent sales to judge how active demand actually is.",
  },
  {
    title: "Why do listing prices vary so much?",
    description:
      "Condition, storage, color, included accessories, timing after launch, and category-wide demand can all shift what sellers ask and what buyers actually pay.",
  },
  {
    title: "What helps a listing perform better?",
    description:
      "Accurate model details, clean photos, honest condition notes, and pricing that reflects the current market usually lead to faster buyer confidence.",
  },
  {
    title: "What should I include in a support request?",
    description:
      "Include the product name, the relevant page URL, screenshots, and a short note describing what you expected versus what happened.",
  },
] as const;

const SUPPORT_SECTIONS = [
  {
    id: "help-center",
    label: "Help Center",
    eyebrow: "Common questions",
    icon: LifeBuoy,
    paragraphs: [
      "Most support needs start with pricing context, listing quality, or how to interpret marketplace signals. The more specific the product and page context, the easier it is to give a useful answer without extra back-and-forth.",
      "If you are troubleshooting a listing or offer flow, it helps to capture the exact step where something changed, stalled, or looked incorrect. That context makes investigation dramatically faster.",
    ],
  },
  {
    id: "contact-us",
    label: "Contact Us",
    eyebrow: "How to reach out well",
    icon: MessageSquareMore,
    paragraphs: [
      `Email ${SUPPORT_EMAIL} and include the essentials up front: device name, listing or product URL, screenshots, and any offer, purchase, or sale identifiers tied to the issue. Clear context is the difference between a quick resolution and a long thread.`,
      "If your question is about trust, suspicious activity, or something that looks unsafe, make that explicit immediately. Safety-related reports should be easy to spot and easy to prioritize.",
    ],
  },
] as const;

function buildSupportMetadata(): SeoMetadata {
  const origin =
    typeof window === "undefined"
      ? SEO_DEFAULT_ORIGIN
      : window.location.origin.replace(/\/+$/, "");

  return {
    title: "Support | Techfare",
    description:
      "Find help for marketplace questions, pricing context, account issues, and the operating principles behind Techfare support.",
    canonicalUrl: `${origin}/support`,
    robots:
      "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
    ogType: "website",
    ogImage: SEO_DEFAULT_SOCIAL_IMAGE,
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "Techfare Support",
        description:
          "Support guidance, marketplace help topics, and policy overviews for Techfare.",
        url: `${origin}/support`,
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: `${origin}/`,
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Support",
            item: `${origin}/support`,
          },
        ],
      },
    ],
  };
}

export default function Support() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SeoTags metadata={buildSupportMetadata()} />
      <Navbar />

      <main className="flex-1 pt-6 pb-16 sm:pt-8">
        <section className="relative overflow-hidden border-b border-border bg-[linear-gradient(180deg,rgba(15,23,42,0.06),rgba(47,102,212,0.04)_38%,transparent)]">
          <div className="container py-10 sm:py-14">
            <div className="max-w-3xl">
              <span className="inline-flex items-center rounded-full border border-slate-300 bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-slate-700">
                Support
              </span>
              <h1 className="mt-4 text-4xl font-semibold tracking-[-0.05em] text-slate-950 sm:text-5xl">
                Practical help for pricing, listings, and marketplace questions.
              </h1>
              <p className="mt-4 max-w-2xl text-base leading-7 text-muted-foreground sm:text-lg">
                Everything here is written to be readable on a first pass:
                common questions, support guidance, and a clear overview of the
                operating principles behind the platform.
              </p>
            </div>

            <div className="mt-7 flex flex-wrap gap-3">
              <Button asChild size="lg" className="rounded-full px-6">
                <Link href="/categories/apple">
                  Explore Markets
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="rounded-full px-6">
                <Link href="/privacy-policy">Read Privacy Policy</Link>
              </Button>
            </div>

            <div className="mt-8 flex flex-wrap gap-2.5">
              {SUPPORT_SECTIONS.map((section) => (
                <a
                  key={section.id}
                  href={`#${section.id}`}
                  className="rounded-full border border-border bg-background/85 px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:border-primary/30 hover:text-primary"
                >
                  {section.label}
                </a>
              ))}
              <Link
                href="/terms-of-service"
                className="rounded-full border border-border bg-background/85 px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:border-primary/30 hover:text-primary"
              >
                Terms of Service
              </Link>
            </div>
          </div>
        </section>

        <section className="container py-8 sm:py-12">
          <div className="grid gap-4 lg:grid-cols-3">
            {SUPPORT_SUMMARY_CARDS.map((item) => {
              const Icon = item.icon;

              return (
                <article
                  key={item.title}
                  className="rounded-[1.5rem] border border-border bg-card p-6 shadow-[0_18px_40px_rgba(15,23,42,0.05)]"
                >
                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h2 className="mt-4 text-lg font-semibold text-foreground">
                    {item.title}
                  </h2>
                  <p className="mt-2 text-sm leading-6 text-muted-foreground">
                    {item.description}
                  </p>
                </article>
              );
            })}
          </div>
        </section>

        <section className="container space-y-5">
          <article
            id="help-center"
            className="scroll-mt-32 rounded-[1.75rem] border border-border bg-card p-6 shadow-[0_18px_40px_rgba(15,23,42,0.04)] sm:p-8"
          >
            <div className="grid gap-6 lg:grid-cols-[minmax(0,0.72fr)_minmax(0,1.28fr)] lg:items-start">
              <div>
                <span className="text-xs font-semibold uppercase tracking-[0.24em] text-primary">
                  Common questions
                </span>
                <div className="mt-3 flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                    <LifeBuoy className="h-5 w-5" />
                  </div>
                  <h2 className="text-2xl font-semibold tracking-[-0.04em] text-slate-950">
                    Help Center
                  </h2>
                </div>
              </div>

              <div>
                <div className="space-y-4 text-sm leading-7 text-muted-foreground sm:text-base">
                  {SUPPORT_SECTIONS[0].paragraphs.map((paragraph) => (
                    <p key={paragraph}>{paragraph}</p>
                  ))}
                </div>

                <div className="mt-6 grid gap-3 md:grid-cols-2">
                  {HELP_CENTER_TOPICS.map((topic) => (
                    <div
                      key={topic.title}
                      className="rounded-[1.25rem] border border-border bg-muted/40 p-5"
                    >
                      <h3 className="text-sm font-semibold text-foreground sm:text-base">
                        {topic.title}
                      </h3>
                      <p className="mt-2 text-sm leading-6 text-muted-foreground">
                        {topic.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </article>

          <article className="rounded-[1.75rem] border border-border bg-card p-6 shadow-[0_18px_40px_rgba(15,23,42,0.04)] sm:p-8">
            <div className="grid gap-6 lg:grid-cols-[minmax(0,0.72fr)_minmax(0,1.28fr)] lg:items-start">
              <div>
                <span className="text-xs font-semibold uppercase tracking-[0.24em] text-primary">
                  Legal resources
                </span>
                <div className="mt-3 flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                    <ShieldCheck className="h-5 w-5" />
                  </div>
                  <h2 className="text-2xl font-semibold tracking-[-0.04em] text-slate-950">
                    Privacy and Terms
                  </h2>
                </div>
              </div>

              <div className="space-y-4 text-sm leading-7 text-muted-foreground sm:text-base">
                <p>
                  The full Privacy Policy and Terms of Service now live on their
                  own pages so they can cover data handling, marketplace rules,
                  account responsibilities, and platform limitations in much more
                  detail than a summary section allows.
                </p>
                <p>
                  If you are evaluating whether to use Techfare for buying,
                  selling, or account-based participation, those documents are
                  the best place to review the formal expectations and
                  disclosures tied to the service.
                </p>

                <div className="flex flex-wrap gap-3 pt-2">
                  <Button asChild className="rounded-full px-5">
                    <Link href="/privacy-policy">
                      Privacy Policy
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </Button>
                  <Button asChild variant="outline" className="rounded-full px-5">
                    <Link href="/terms-of-service">Terms of Service</Link>
                  </Button>
                </div>
              </div>
            </div>
          </article>

          {SUPPORT_SECTIONS.slice(1).map((section) => {
            const Icon = section.icon;

            return (
              <article
                key={section.id}
                id={section.id}
                className="scroll-mt-32 rounded-[1.75rem] border border-border bg-card p-6 shadow-[0_18px_40px_rgba(15,23,42,0.04)] sm:p-8"
              >
                <div className="grid gap-6 lg:grid-cols-[minmax(0,0.72fr)_minmax(0,1.28fr)] lg:items-start">
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-[0.24em] text-primary">
                      {section.eyebrow}
                    </span>
                    <div className="mt-3 flex items-center gap-3">
                      <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                        <Icon className="h-5 w-5" />
                      </div>
                      <h2 className="text-2xl font-semibold tracking-[-0.04em] text-slate-950">
                        {section.label}
                      </h2>
                    </div>
                  </div>

                  <div className="space-y-4 text-sm leading-7 text-muted-foreground sm:text-base">
                    {section.paragraphs.map((paragraph) => (
                      <p key={paragraph}>{paragraph}</p>
                    ))}

                    {section.id === "contact-us" ? (
                      <div className="flex flex-wrap gap-3 pt-2">
                        <a
                          href={`mailto:${SUPPORT_EMAIL}`}
                          className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
                        >
                          Email {SUPPORT_EMAIL}
                          <ArrowRight className="h-4 w-4" />
                        </a>
                        <a
                          href={`mailto:${SUPPORT_EMAIL}?subject=Techfare%20Support%20Request`}
                          className="inline-flex items-center gap-2 rounded-full border border-border px-5 py-2.5 text-sm font-medium text-foreground transition-colors hover:border-primary/30 hover:text-primary"
                        >
                          Start a Support Request
                        </a>
                      </div>
                    ) : null}
                  </div>
                </div>
              </article>
            );
          })}
        </section>
      </main>

      <Footer />
    </div>
  );
}
